import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  applyActionCode,
  Auth,
  checkActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getMultiFactorResolver,
  multiFactor,
  MultiFactorError,
  MultiFactorInfo,
  MultiFactorResolver,
  onIdTokenChanged,
  ParsedToken,
  PasswordValidationStatus,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  TotpMultiFactorGenerator,
  TotpSecret,
  updatePassword,
  updateProfile,
  User,
  UserCredential,
  validatePassword,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { FIREBASE_HANDLES } from './firebase.providers';
import { ViewAsService } from '../orgs/view-as.service';
import type { OrgRole } from '../orgs/orgs.types';

/**
 * Custom claims attached to the Firebase ID token.
 *
 * Set out-of-band by Cloud Functions (`syncUserClaims` for `internalAdmin`,
 * `syncOrgMemberClaims` for `orgRoles` / `activeOrgId`) and refreshed
 * automatically the next time the SDK rotates the ID token.
 */
/** What `sendVerificationEmail` did: mailed, or nothing to confirm. */
export type VerificationEmailOutcome = 'sent' | 'alreadyVerified';

/** What `notifyTwoFactorChange` found: a real change, and the state now. */
export interface TwoFactorNoticeResult {
  readonly changed: boolean;
  readonly enabled: boolean;
}

/** The name an authenticator-app factor gets when the person names none. */
export const TOTP_DISPLAY_NAME = 'Authenticator app';

/** Longest device name a person can give a factor. */
export const TOTP_DEVICE_NAME_MAX = 40;

/**
 * The issuer shown in the authenticator app next to the account:
 * `GHOSTS` in production, `GHOSTS [staging]` on staging.
 */
export const TOTP_ISSUER = environment.authenticatorIssuer;

/**
 * The name a factor is enrolled under: what the person typed, trimmed
 * and capped, or {@link TOTP_DISPLAY_NAME}.
 */
export function totpFactorName(deviceName: string | null | undefined): string {
  const name = (deviceName ?? '').trim().replace(/\s+/g, ' ').slice(0, TOTP_DEVICE_NAME_MAX);
  return name || TOTP_DISPLAY_NAME;
}

export interface GhostsClaims {
  /** True when the user is part of the internal team (Ghosts staff/admins). */
  readonly internalAdmin?: boolean;
  /**
   * Map of org ID → role for every org this user is an active or invited
   * member of. Drives `orgPermissionGuard`, `[appHasPermission]`, and
   * Firestore rules.
   */
  readonly orgRoles?: Readonly<Record<string, OrgRole>>;
  /**
   * Last org the user selected via the topbar switcher. Mirrored from
   * `users/{uid}.activeOrgId` by `syncOrgMemberClaims` so it survives
   * sign-in on a fresh device.
   */
  readonly activeOrgId?: string;
  /**
   * Written by `beforeUserCreated` for a self-serve signup and cleared the
   * moment membership is granted. It confers NOTHING — it exists so the
   * app can tell a new signup apart from an invited user whose claims have
   * not synced yet, a distinction "has no orgRoles" cannot make.
   */
  readonly signupPending?: boolean;
}

/**
 * Wraps Firebase Auth and exposes the current user + custom claims as
 * signals so components and route guards can react synchronously without
 * managing subscriptions themselves.
 *
 * The service drives off `onIdTokenChanged` (rather than `onAuthStateChanged`)
 * so that custom-claim updates — issued by the Cloud Function trigger that
 * mirrors `users/{uid}` to Auth claims — propagate automatically the next
 * time the SDK refreshes the ID token.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly handles = inject(FIREBASE_HANDLES);
  private readonly auth: Auth = this.handles.auth;
  private readonly destroyRef = inject(DestroyRef);
  private readonly viewAs = inject(ViewAsService);

  /** `undefined` until the first auth state callback fires; then `User | null`. */
  private readonly _user = signal<User | null | undefined>(undefined);
  private readonly _claims = signal<GhostsClaims>({});

  /** Current user (`null` if signed out, `undefined` while still resolving). */
  readonly user = this._user.asReadonly();

  /** Custom claims from the ID token (empty object if signed out). */
  readonly claims = this._claims.asReadonly();

  /** True once the first auth state has been resolved (signed in or out). */
  readonly initialized = computed(() => this._user() !== undefined);

  /** True iff a user is signed in. */
  readonly isSignedIn = computed(() => !!this._user());

  /**
   * True iff the ACCOUNT carries the internalAdmin custom claim.
   *
   * This is the identity. Use it only to decide who may be offered the
   * "view as role" control (#253) — every gate on what the UI should show
   * wants {@link isInternalAdmin}, which answers the same question through
   * whatever lens is on.
   */
  readonly isInternalAdminAccount = computed(() => this._claims().internalAdmin === true);

  /**
   * True iff the UI should show internal-team affordances right now.
   *
   * ⚠️ Not the same question as {@link isInternalAdminAccount}: an internal
   * admin viewing the product as a member is still an internal admin, and
   * this is deliberately false for them, so the internal nav, the internal
   * routes and the internal-only panels all disappear together. They have to:
   * a lens you can see past is not a lens. The server has never read this.
   */
  readonly isInternalAdmin = computed(() => this.isInternalAdminAccount() && !this.viewAs.active());

  /**
   * True once both the user AND their custom claims have been resolved
   * for the current session. Guards that depend on role information must
   * wait for this rather than `initialized`, otherwise they may evaluate
   * `isInternalAdmin()` against an empty claims object.
   */
  readonly claimsInitialized = computed(
    () => this._user() !== undefined && this._claimsForUid() === currentUid(this._user()),
  );

  /**
   * Tracks which user the current `_claims` value belongs to, so async
   * `getIdTokenResult()` results from a previous user can't be applied to
   * a new one (out-of-order race when sign-out → sign-in fires fast).
   */
  private readonly _claimsForUid = signal<string | null | undefined>(undefined);

  /** Observable mirror of token changes for RxJS-heavy code paths. */
  readonly user$ = new Observable<User | null>((subscriber) => {
    return onIdTokenChanged(
      this.auth,
      (user) => subscriber.next(user),
      (err) => subscriber.error(err),
    );
  });

  constructor() {
    // Drive the user/claims signals off Firebase ID token changes for the
    // lifetime of the root injector. Using onIdTokenChanged (not
    // onAuthStateChanged) means refreshed claims surface without a forced
    // re-login.
    this.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      void this.applyUser(user);
    });
  }

  /**
   * Password sign-in. For an account with two-factor sign-in it rejects
   * with `auth/multi-factor-auth-required` after a correct password; the
   * login screen then asks for the code ({@link multiFactorResolver}).
   */
  signInWithEmail(email: string, password: string): Promise<UserCredential> {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  /**
   * Creates an account. The `beforeUserCreated` blocking function decides
   * whether the address's domain is allowed; the account it creates carries
   * no org membership until the address is verified.
   *
   * Firebase signs the new user in as part of this call, so the caller can
   * immediately `sendVerificationEmail()` — which is the whole reason the
   * sign-in gate lets unverified sessions through.
   */
  signUpWithEmail(email: string, password: string): Promise<UserCredential> {
    return createUserWithEmailAndPassword(this.auth, email, password);
  }

  /** Sets the display name on the currently signed-in user. */
  async setDisplayName(displayName: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) {
      return;
    }
    await updateProfile(user, { displayName });
  }

  /**
   * Sends (or re-sends) the address-verification email.
   *
   * Through our `sendVerificationEmail` callable, not the SDK's
   * `sendEmailVerification`: the app sends its own branded email, from our
   * domain, with a link on our domain. The callable limits sends per
   * account and refuses with `functions/resource-exhausted` and a friendly
   * message (`verificationEmailErrorMessage` shows it). An address that is
   * already confirmed is not mailed again, and the outcome says so: the
   * caller's `emailVerified` may simply be stale.
   */
  async sendVerificationEmail(): Promise<VerificationEmailOutcome> {
    if (!this.auth.currentUser) {
      throw new Error('Not signed in.');
    }
    const callable = httpsCallable<
      Record<string, never>,
      { sent: true } | { alreadyVerified: true }
    >(this.handles.functions, 'sendVerificationEmail');
    const { data } = await callable({});
    return 'alreadyVerified' in data && data.alreadyVerified ? 'alreadyVerified' : 'sent';
  }

  /**
   * Proves the signed-in person is still at the keyboard by asking for the
   * password again. Email change, password change and two-factor changes
   * need a sign-in from the last few minutes; this resets that clock and
   * refreshes the ID token (its `auth_time` is what `requestEmailChange`
   * checks).
   *
   * Errors pass through untouched (`auth/wrong-password`,
   * `auth/multi-factor-auth-required`, ...): the re-authentication dialog
   * words them, and takes the second-factor step for the last one.
   */
  reauthenticate(password: string): Promise<UserCredential> {
    const user = this.auth.currentUser;
    if (!user?.email) {
      return Promise.reject(new Error('Not signed in.'));
    }
    return reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
  }

  /**
   * The pending sign-in behind `auth/multi-factor-auth-required` (from a
   * sign-in or a re-authentication): its `hints` name the second factors,
   * and {@link resolveTotpSignIn} finishes it. It expires after a few
   * minutes (`auth/invalid-multi-factor-session` and friends).
   */
  multiFactorResolver(error: MultiFactorError): MultiFactorResolver {
    return getMultiFactorResolver(this.auth, error);
  }

  /**
   * Finishes a pending sign-in or re-authentication with the 6-digit code
   * of the authenticator-app factor `enrollmentId`. Safe to call again
   * with the same resolver after a blocking-function deadline (the
   * password is not needed again); a TOTP code is good once, though.
   */
  resolveTotpSignIn(
    resolver: MultiFactorResolver,
    enrollmentId: string,
    code: string,
  ): Promise<UserCredential> {
    return resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(enrollmentId, code));
  }

  /**
   * The second factors on the signed-in account, as Firebase last loaded
   * them (empty when signed out). The User object is updated in place by
   * enroll / unenroll, so callers re-read this after a change.
   */
  twoFactorFactors(): readonly MultiFactorInfo[] {
    const user = this.auth.currentUser;
    return user ? multiFactor(user).enrolledFactors : [];
  }

  /**
   * Starts turning on two-factor sign-in: a fresh TOTP secret, shown to
   * the person as a QR code (`secret.generateQrCodeUrl(email,
   * TOTP_ISSUER)`) and as text. Needs a recent sign-in and a verified
   * address (`auth/requires-recent-login`, `auth/unverified-email`), and a
   * project with TOTP enabled (`auth/operation-not-allowed` otherwise).
   * The secret expires at `secret.enrollmentCompletionDeadline`.
   */
  async startTotpEnrollment(): Promise<TotpSecret> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not signed in.');
    const session = await multiFactor(user).getSession();
    return TotpMultiFactorGenerator.generateSecret(session);
  }

  /**
   * Finishes turning on two-factor sign-in with the first code the
   * authenticator app shows for `secret`, under the person's name for the
   * device ({@link totpFactorName}). Wrong code:
   * `auth/invalid-verification-code`.
   */
  async finishTotpEnrollment(
    secret: TotpSecret,
    code: string,
    deviceName?: string | null,
  ): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not signed in.');
    await multiFactor(user).enroll(
      TotpMultiFactorGenerator.assertionForEnrollment(secret, code),
      totpFactorName(deviceName),
    );
  }

  /**
   * Turns two-factor sign-in off: removes every second factor on the
   * account. Needs a recent sign-in. Firebase may end the session when a
   * factor goes, so callers check it afterwards with
   * {@link confirmSessionAlive}.
   */
  async removeTwoFactor(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not signed in.');
    const mfa = multiFactor(user);
    for (const factor of [...mfa.enrolledFactors]) {
      await mfa.unenroll(factor);
    }
  }

  /**
   * Asks the server to mail the account's owner about a two-factor change.
   * The server reads the Auth record and mails only when the state really
   * changed since it last told them, so calling this is always safe.
   */
  async notifyTwoFactorChange(): Promise<TwoFactorNoticeResult> {
    const callable = httpsCallable<Record<string, never>, TwoFactorNoticeResult>(
      this.handles.functions,
      'notifyTwoFactorChange',
    );
    const { data } = await callable({});
    return { changed: data.changed === true, enabled: data.enabled === true };
  }

  /**
   * Sets a new password. Firebase refuses with `auth/requires-recent-login`
   * unless the person signed in or re-authenticated recently.
   */
  updatePassword(newPassword: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) {
      return Promise.reject(new Error('Not signed in.'));
    }
    return updatePassword(user, newPassword);
  }

  /**
   * Asks for the account to move to `newEmail`. Nothing changes until the
   * link mailed to the new address is followed. The callable refuses with
   * `functions/failed-precondition` (details `{reason: 'recent-sign-in'}`)
   * when the last sign-in is older than five minutes: re-authenticate and
   * retry. It never says whether the address belongs to another account.
   */
  async requestEmailChange(newEmail: string): Promise<void> {
    if (!this.auth.currentUser) {
      throw new Error('Not signed in.');
    }
    const callable = httpsCallable<{ newEmail: string }, { sent: true }>(
      this.handles.functions,
      'requestEmailChange',
    );
    await callable({ newEmail });
  }

  /**
   * Asks for a new invitation to an org, from an expired invitation link;
   * nobody needs to be signed in. The callable answers the same whatever
   * it finds (it never says whether the address has an account or an
   * invitation), so this resolves without a result. It rejects only for
   * malformed input (`functions/invalid-argument`) or a request that did
   * not get through.
   */
  async resendInvitation(email: string, orgId: string): Promise<void> {
    const callable = httpsCallable<{ email: string; orgId: string }, { ok: true }>(
      this.handles.functions,
      'resendInvitation',
    );
    await callable({ email, orgId });
  }

  /**
   * Brings the copies of the address (`users/{uid}`, member docs, a pending
   * join request) in line with the Auth record. Idempotent; resolves to
   * whether anything changed. `AccountEmailSyncService` calls it.
   */
  async syncAccountEmail(): Promise<boolean> {
    const callable = httpsCallable<Record<string, never>, { changed: boolean }>(
      this.handles.functions,
      'syncAccountEmail',
    );
    const { data } = await callable({});
    return data.changed === true;
  }

  /**
   * Re-fetches the user from Firebase and reports whether the address is
   * now confirmed. Provisions NOTHING.
   *
   * Verification happens in the user's mail client, out of band, so nothing
   * in this tab knows about it until we ask.
   *
   * ⚠️ A refresh is not a sign-in. `reload()` and `getIdToken(true)` do not
   * fire `beforeUserSignedIn`, which is the only place membership is
   * granted — that hook runs on authentication events, not token
   * management. So a confirmed address still carries no org until the user
   * signs in again, and the caller must send them through sign-in rather
   * than into the app on the strength of this returning true.
   *
   * It answers for the user it reloaded: when another tab signed in as
   * someone else during the round trip, that other account's flag is not
   * this one's, and the answer is false.
   */
  async reloadUser(): Promise<boolean> {
    const user = this.auth.currentUser;
    if (!user) {
      return false;
    }
    await user.reload();
    await this.applyUser(this.auth.currentUser);
    return this.auth.currentUser?.uid === user.uid && user.emailVerified === true;
  }

  /**
   * Checks a password against the project's live password policy.
   *
   * Fetched from Firebase rather than hardcoded: the policy is enforced
   * server-side (currently 16 characters, mixed case, a digit) and a
   * client-side copy would drift and start lying to people.
   */
  validatePassword(password: string): Promise<PasswordValidationStatus> {
    return validatePassword(this.auth, password);
  }

  /**
   * Is this browser's session still good? Forces a token refresh, which
   * Firebase refuses once the account's sessions were revoked (an email
   * or password change). A refused session is signed out here.
   */
  async confirmSessionAlive(): Promise<boolean> {
    const user = this.auth.currentUser;
    if (!user) {
      return false;
    }
    try {
      await user.getIdToken(true);
      return true;
    } catch {
      await this.signOut();
      return false;
    }
  }

  /**
   * Forces a new ID token and resolves once `claims()` carries it.
   *
   * For a session whose memberships changed under it: the claim only moves
   * when the token is refreshed, which Firebase otherwise does on its own up
   * to an hour later. Rejects as `getIdToken(true)` does; a revoked session
   * (`auth/user-token-expired`) has been signed out by the SDK by then.
   */
  async refreshClaims(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;
    await user.getIdToken(true);
    // Signed out (or another account in) while that was in flight: firebase
    // leaves the old User object working, and applying it would put a
    // signed-out person back into the app.
    if (this.auth.currentUser !== user) return;
    // The token listener applies it too, but asynchronously; applying it
    // here means the caller sees the new claims when this resolves.
    await this.applyUser(user);
  }

  /** Sends a password-reset email via Firebase Auth. */
  sendPasswordReset(email: string): Promise<void> {
    return sendPasswordResetEmail(this.auth, email);
  }

  /**
   * The address a password-reset code (an emailed reset or invitation link)
   * belongs to. Rejects for a used, expired or unknown code
   * (`auth/invalid-action-code`, `auth/expired-action-code`, ...).
   */
  verifyPasswordResetCode(code: string): Promise<string> {
    return verifyPasswordResetCode(this.auth, code);
  }

  /** Sets the password of the code's account, and spends the code. Signs nobody in. */
  confirmPasswordReset(code: string, password: string): Promise<void> {
    return confirmPasswordReset(this.auth, code, password);
  }

  /** Carries out an emailed action (confirm an address, undo a change), and spends the code. */
  applyActionCode(code: string): Promise<void> {
    return applyActionCode(this.auth, code);
  }

  /**
   * What an action code would do, without spending it: `email` is the
   * address it acts on (for `recoverEmail`, the one it restores), and
   * `previousEmail` the one it replaces, when Firebase names them.
   */
  async checkActionCode(
    code: string,
  ): Promise<{ email: string | null; previousEmail: string | null }> {
    const { data } = await checkActionCode(this.auth, code);
    return { email: data.email ?? null, previousEmail: data.previousEmail ?? null };
  }

  signOut(): Promise<void> {
    // The lens belongs to a session, not to the browser: the next person to
    // sign in here must not inherit someone else's borrowed role (#253).
    this.viewAs.clear();
    return signOut(this.auth);
  }

  /**
   * Bumped by every `applyUser` call. Only the newest call may apply the
   * claims it read: two refreshes of the SAME account can resolve out of
   * order, and an older token must not restore a claim a newer one dropped.
   */
  private applyGeneration = 0;

  private async applyUser(user: User | null): Promise<void> {
    const generation = ++this.applyGeneration;
    // Reset claims synchronously whenever the user identity changes so we
    // never expose the previous user's claims (e.g. internalAdmin) to the
    // new one between `_user.set(...)` and the async token resolve below.
    const targetUid = currentUid(user);
    if (currentUid(this._user()) !== targetUid) {
      this._claims.set({});
      this._claimsForUid.set(undefined);
      // A lens belongs to the account that put it on (#253), not to the
      // browser: a different account never inherits one.
      this.viewAs.clear();
    }
    this._user.set(user);

    if (!user) {
      this._claims.set({});
      this._claimsForUid.set(null);
      return;
    }

    let parsed: GhostsClaims;
    try {
      const tokenResult = await user.getIdTokenResult();
      parsed = toGhostsClaims(tokenResult.claims);
    } catch {
      // If we can't read claims, fall back to empty rather than blocking.
      parsed = {};
    }

    // Drop the result if a later applyUser() call started while we were
    // awaiting the token — for another user, or for this one with a newer
    // token. That call owns the next claims assignment.
    if (generation !== this.applyGeneration || currentUid(this._user()) !== targetUid) {
      return;
    }
    this._claims.set(parsed);
    this._claimsForUid.set(targetUid);
    // The lens is an internal-admin control. An account whose claim is gone
    // (revoked mid-session, seen at the next token refresh) stops wearing
    // one. The tab's acting-as org needs no such step: `OrgService` gates
    // it on the claim at every read.
    if (parsed.internalAdmin !== true) this.viewAs.clear();
  }
}

function currentUid(user: User | null | undefined): string | null | undefined {
  if (user === undefined) return undefined;
  return user?.uid ?? null;
}

function toGhostsClaims(claims: ParsedToken): GhostsClaims {
  const orgRolesRaw = claims['orgRoles'];
  const orgRoles =
    orgRolesRaw && typeof orgRolesRaw === 'object' && !Array.isArray(orgRolesRaw)
      ? (orgRolesRaw as Record<string, OrgRole>)
      : undefined;
  const activeOrgId =
    typeof claims['activeOrgId'] === 'string' ? (claims['activeOrgId'] as string) : undefined;
  return {
    internalAdmin: claims['internalAdmin'] === true,
    orgRoles,
    activeOrgId,
    // `isAwaitingEmailConfirmation` keys on this; dropping it here made
    // that guard unreachable.
    signupPending: claims['signupPending'] === true ? true : undefined,
  };
}
